/**
 * Semantic tour targets.
 *
 * A host marks an element with `data-tour-id="shell.new-tab"`; the renderer
 * resolves that attribute and nothing else. The indirection is the point:
 * definitions never carry a CSS selector, so they cannot couple to generated
 * class names, `nth-child` positions or visible text, and a JSON file can never
 * address an element the product did not deliberately expose.
 */

export const TOUR_TARGET_ATTRIBUTE = "data-tour-id";

/** The attribute props a host spreads onto the element it is exposing. */
export function tourTarget(id: string): { readonly [k: string]: string } {
  return { [TOUR_TARGET_ATTRIBUTE]: id };
}

function escapeAttributeValue(value: string): string {
  // The ids this receives are already constrained by `TourTargetIdSchema`, so
  // this is belt-and-braces rather than the gate — but a selector built by
  // string concatenation is exactly the kind of thing that stops being safe
  // when somebody widens the schema later.
  return value.replace(/["\\]/g, "\\$&");
}

export function findTourTarget(
  id: string,
  root: ParentNode = document,
): HTMLElement | null {
  return (
    Array.from(
      root.querySelectorAll<HTMLElement>(
        `[${TOUR_TARGET_ATTRIBUTE}="${escapeAttributeValue(id)}"]`,
      ),
    ).find(isVisibleTourTarget) ?? null
  );
}

/** Reject hidden surfaces consistently during discovery and measurement. */
export function isVisibleTourTarget(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  for (
    let node: HTMLElement | null = element;
    node;
    node = node.parentElement
  ) {
    if (node.hidden || node.getAttribute("aria-hidden") === "true")
      return false;
    const style = getComputedStyle(node);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.visibility === "collapse" ||
      style.opacity === "0"
    )
      return false;
  }
  return true;
}

/**
 * Which of `ids` are on the page right now.
 *
 * VISIBILITY matters, not just presence: a target inside a collapsed panel has
 * a zero-size rect, and spotlighting it would put a card next to nothing. A
 * zero-size element is therefore treated as absent, which routes the step
 * through the definition's own missing-target policy.
 */
export function availableTourTargets(
  ids: readonly string[],
  root: ParentNode = document,
): Set<string> {
  const present = new Set<string>();
  for (const id of ids) {
    const element = findTourTarget(id, root);
    if (!element) continue;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    present.add(id);
  }
  return present;
}
